import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { LocalAudioTrack, LocalVideoTrack, NetworkQualityLevel, VideoProcessor, VideoTrack } from "twilio-video";
import { createContext } from "use-context-selector";
import { getExistingVideoTrack } from "../components/helpers/getExistingTrack";
import { useRemoteParticipant } from "../hooks/useRemoteParticipant";
import { useRoom } from "../hooks/useRoom";
import { getTrack } from "../utils/getTrack";
import {
  GaussianBlurBackgroundProcessor,
  Pipeline,
  VirtualBackgroundProcessor,
} from "@twilio/video-processors";
import livingRoomImage from "../assets/images/effects/living_room.jpg";
import officeImage from "../assets/images/effects/office.jpg";
import { showToast } from "../utils/showToast";
import { setTracksCleanup } from "../index";

type DefaultBackgrounds = "living_room" | "office" | "blur";

export type LocalParticipantContextType = {
  audioOff: boolean;
  videoOff: boolean;
  handleAudio: (track?: LocalAudioTrack) => void;
  handleVideo: (track?: LocalVideoTrack) => void;
  screenTrack: LocalVideoTrack | undefined;
  sharingScreen: boolean;
  showEffectsModal: boolean;
  handleShareScreen: () => void;
  setShowEffectsModal: (show: boolean) => void;
  networkQualityLevelLocal: number;
  setNetworkQualityLevelLocal: (value: number) => void;
  handleChangeBackground: (background: string | null) => Promise<void>;
  setAudioOff: (audioOff: boolean) => void;
  setVideoOff: (videoOff: boolean) => void;
};

export const LocalParticipantContext = createContext({} as LocalParticipantContextType);

export const LocalParticipantContextProvider = ({ children }: { children: any }) => {
  const { room, localTracks, handleChangeFocused, currentFocused, handleFocusedVideoOff } =
  useRoom();
  const { remoteParticipant } = useRemoteParticipant();
  const [backgroundEffect, setBackgroundEffect] = useState<DefaultBackgrounds | string | null>(null);
  
  const [videoOff, setVideoOff] = useState<boolean>(false);
  const [audioOff, setAudioOff] = useState<boolean>(false);
  
  const [showEffectsModal, setShowEffectsModal] = React.useState<boolean>(false);
  const [sharingScreen, setSharingScreen] = useState<boolean>(false);
  const [screenTrack, setScreenTrack] = useState<LocalVideoTrack>();
  
  const [networkQualityLevelLocal, setNetworkQualityLevelLocal] = useState<number>(0);
  
  const pipeline = Pipeline.WebGL2;
  const assetsPath = process.env.REACT_APP_FRONT_FILES || '/';
  const debounce = true;

  let virtualBackgroundProcessor: VirtualBackgroundProcessor | undefined;
  let gaussianProcessor: GaussianBlurBackgroundProcessor | undefined;

  const registered = useRef(false);

  // Handle leave room
  useEffect(() => {
    if (!room) {
      setSharingScreen(false);
    }

    setTracksCleanup(() => {
      if (room) {
      room.localParticipant.tracks.forEach((pub) => {
          if (pub.track.kind !== "data") {
            pub.track.stop();
            pub.track.detach().forEach((el) => el.remove());
          }
        });
        room.disconnect();
      }
    });
  
    registered.current = true;
  }, [room]);

  const handleNetworkQualityLevelChanged = useCallback((qualityLevel: NetworkQualityLevel) => {
    setNetworkQualityLevelLocal(qualityLevel);
  }, []);

  useEffect(() => {
    if (room && room.localParticipant) {
      room.localParticipant.on("networkQualityLevelChanged", handleNetworkQualityLevelChanged);
    }

    return () => {
      if (room && room.localParticipant) {
        room.localParticipant.off("networkQualityLevelChanged", handleNetworkQualityLevelChanged);
      }
    };
  }, [room?.localParticipant]);

  useEffect(() => {
    if (currentFocused === "local") handleFocusedVideoOff(videoOff);
  }, [currentFocused]);

  const loadImage = (name: DefaultBackgrounds | string): Promise<HTMLImageElement> => {
    const source = name === "livingRoom" ? livingRoomImage : name === "office" ? officeImage : name;
    return new Promise((resolve) => {
      const image = new Image();
      image.src = source;
      image.onload = () => resolve(image);
    });
  };

  const handleChangeBackground = async (background: DefaultBackgrounds | string | null) => {
    let track: LocalVideoTrack | undefined;
    for (const localTrack of localTracks) {
      if (localTrack.kind === "video") {
        track = localTrack;
      }
    }
    if (!track) {
      return;
    }
    if (track.processor) {
      track.removeProcessor(track.processor);
    }
    if (background === null) {
      setBackgroundEffect(null);
      return;
    }
    if (background === "blur") {
      if (!gaussianProcessor) {
        gaussianProcessor = new GaussianBlurBackgroundProcessor({
          assetsPath,
          pipeline,
          debounce,
        });
        await gaussianProcessor.loadModel();
      }
      track.addProcessor(gaussianProcessor, {
        inputFrameBufferType: "video",
        outputFrameBufferContextType: "webgl2",
      });
      setBackgroundEffect("blur");
    } else {
      if (!virtualBackgroundProcessor) {
        const backgroundImage = await loadImage("livingRoom");
        virtualBackgroundProcessor = new VirtualBackgroundProcessor({
          assetsPath,
          backgroundImage,
          pipeline,
          debounce,
        });
        await virtualBackgroundProcessor.loadModel();
      }
      virtualBackgroundProcessor.backgroundImage = await loadImage(background);
      track.addProcessor(virtualBackgroundProcessor, {
        inputFrameBufferType: "video",
        outputFrameBufferContextType: "webgl2",
      });
      setBackgroundEffect(background);
    }
  };

  /**
   * Habilita/desabilita video participante local
   */
  const handleVideo = useCallback(
    async (track?: LocalVideoTrack) => {
      if (room) {
        const videoTrackPublication = Array.from(room.localParticipant.videoTracks.values()).find(
          (publication) => publication.track.name === "camera"
        );
        const videoTrack = videoTrackPublication?.track as LocalVideoTrack;
  
        if (!videoTrack) return;
  
        if (videoOff) {
          await videoTrack.restart();
          videoTrack.enable();
          setVideoOff(false);
          if (backgroundEffect) {
            await handleChangeBackground(backgroundEffect);
          }
          if (currentFocused === "local") {
            handleFocusedVideoOff(false);
          }
        } else {
          if (videoTrack.processor) {
            await videoTrack.removeProcessor(videoTrack.processor);
          }
          await videoTrack.restart();
          videoTrack.stop();
          videoTrack.disable();
          setVideoOff(true);
          if (currentFocused === "local") {
            handleFocusedVideoOff(true);
          }
        }
      } else if (track) {
        if (videoOff) {
          await track.restart();
          track.enable();
          setVideoOff(false);
          if (backgroundEffect) {
            await handleChangeBackground(backgroundEffect);
          }
        } else {
          if (track.processor) {
            await track.removeProcessor(track.processor);
          }
          await track.restart();
          track.stop();
          track.disable();
          setVideoOff(true);
        }
      }
    },
    [videoOff, room, currentFocused, backgroundEffect]
  );

  useEffect(() => {
    const interval = setInterval(async () => {
      const pub = Array.from(room?.localParticipant?.videoTracks.values() || []).find(
        (p) => p.track.name === "camera"
      );
      const track = pub?.track as LocalVideoTrack | undefined;
  
      if (!track) return;
  
      if (videoOff && (track.isEnabled)) {
        try {
          if (track.processor) await track.removeProcessor(track.processor);
          track.stop();
          track.disable();
        } catch (err) {
          console.error("Erro ao forçar stop:", err);
        }
      }
  
      if (!videoOff && (!track.isEnabled)) {
        try {
          await track.restart();
          track.enable();
          if (backgroundEffect) await handleChangeBackground(backgroundEffect);
        } catch (err) {
          console.error("Erro ao reativar câmera:", err);
        }
      }
    }, 1000);
  
    return () => clearInterval(interval);
  }, [room, videoOff, backgroundEffect]);

  

  /**
   * Habilita/desabilita audio do participante local
   */
  const handleAudio = useCallback(
    (track?: LocalAudioTrack) => {
      if (room) {
        if (audioOff) {
          room.localParticipant.audioTracks.forEach((audioTrack) => {
            audioTrack.track.restart();
            audioTrack.track.enable();
          });
          setAudioOff(false);
        } else {
          room.localParticipant.audioTracks.forEach((audioTrack) => {
            audioTrack.track.disable();
          });
          setAudioOff(true);
        }
      } else if (track) {
        if (audioOff) {
          track.enable();
          setAudioOff(false);
        } else {
          track.disable();
          setAudioOff(true);
        }
      }
    },
    [audioOff, room]
  );

  /**
   * Ao ser chamada, a função gerencia o compartilhamento de tela.
   * Gerando o track de video da tela e publicando na sala.
   */
  const handleShareScreen = useCallback(() => {
    // INICIAR compartilhamento
    if (!screenTrack) {
      const isSomeoneElseSharing = Array.from(room?.participants.values() || []).some((participant) =>
        Array.from(participant.videoTracks.values()).some((pub) => pub.track?.name === "screen")
      );
  
      const isLocalSharing = Array.from(room?.localParticipant.videoTracks.values() || []).some(
        (pub) => pub.track?.name === "screen"
      );
  
      if (isSomeoneElseSharing || isLocalSharing) {
        showToast("error", {
          text: "Já existe alguém compartilhando a tela no momento.",
          timer: 4000,
        });
        return;
      }
  
      navigator.mediaDevices
        .getDisplayMedia({
          video: { frameRate: 30 },
          audio: false,
        })
        .then((stream: MediaStream) => {
          setSharingScreen(true);
          const localVideoTrack = new LocalVideoTrack(stream.getTracks()[0], {
            name: "screen",
            logLevel: "off",
          });
          handleChangeFocused("screen", localVideoTrack);
          setScreenTrack(localVideoTrack);
        })
        .catch(() => {
          setSharingScreen(false);
        });
    }
  
    // PARAR compartilhamento
    else {
      screenTrack.stop();
      setScreenTrack(undefined);
      setSharingScreen(false);
      if (room) {
        room.localParticipant.unpublishTrack(screenTrack);
        handleChangeFocused(
          remoteParticipant ? "remote" : "local",
          getTrack(
            "camera",
            getExistingVideoTrack(remoteParticipant || room.localParticipant)
          ) as VideoTrack
        );
      }
    }
  }, [screenTrack, room, remoteParticipant, handleChangeFocused]);
  

  useEffect(() => {
    if (screenTrack && room) {
      room.localParticipant.publishTrack(screenTrack);
      screenTrack.mediaStreamTrack.onended = () => handleShareScreen();
    }
  }, [screenTrack]);

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <LocalParticipantContext.Provider
      value={{
        audioOff,
        videoOff,
        setAudioOff,
        setVideoOff,
        handleVideo,
        handleAudio,
        screenTrack,
        sharingScreen,
        showEffectsModal,
        handleShareScreen,
        setShowEffectsModal,
        networkQualityLevelLocal,
        setNetworkQualityLevelLocal,
        handleChangeBackground,
      }}
    >
      {children}
    </LocalParticipantContext.Provider>
  );
};
